import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Plus, Upload, Download, Search, Edit, Trash2, Eye, MessageCircle, Image, Volume2, FileVideo, File, Clock, UserPlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  try {
    const now = new Date();
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'agora';
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  } catch { return ''; }
}

function getUrgencyColor(lastMsgAt) {
  if (!lastMsgAt) return '';
  try {
    const now = new Date();
    const d = new Date(lastMsgAt);
    if (isNaN(d.getTime())) return '';
    const mins = Math.floor((now - d) / 60000);
    if (mins <= 5) return 'bg-green-500';
    if (mins <= 30) return 'bg-yellow-400';
    return 'bg-red-500';
  } catch { return ''; }
}

function msgTypeIcon(type) {
  switch (type) {
    case 'audio': return <Volume2 className="w-3 h-3 inline mr-1 opacity-70" />;
    case 'image': return <Image className="w-3 h-3 inline mr-1 opacity-70" />;
    case 'video': return <FileVideo className="w-3 h-3 inline mr-1 opacity-70" />;
    case 'document': return <File className="w-3 h-3 inline mr-1 opacity-70" />;
    default: return null;
  }
}

const statusLabels = {
  sem_contato: 'Sem Contato',
  agendamento: 'Agendamento',
  venda: 'Venda',
  audio_enviado: 'Audio Enviado',
  nao_interesse: 'Nao Interesse',
  trote: 'Trote',
  reprovado: 'Reprovado',
};

const statusColors = {
  sem_contato: 'bg-slate-100 text-slate-800',
  agendamento: 'bg-indigo-100 text-indigo-800',
  venda: 'bg-emerald-100 text-emerald-800',
  audio_enviado: 'bg-violet-100 text-violet-800',
  nao_interesse: 'bg-amber-100 text-amber-800',
  trote: 'bg-orange-100 text-orange-800',
  reprovado: 'bg-red-100 text-red-800',
};

const sourceLabels = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  manual: 'Manual',
  import: 'Importacao',
  whatsapp: 'WhatsApp',
  website: 'Website',
  indicacao: 'Indicacao'
};

const sourceColors = {
  facebook: 'bg-blue-100 text-blue-800',
  instagram: 'bg-pink-100 text-pink-800',
  manual: 'bg-slate-100 text-slate-800',
  import: 'bg-amber-100 text-amber-800',
  whatsapp: 'bg-green-100 text-green-800',
  website: 'bg-cyan-100 text-cyan-800',
  indicacao: 'bg-purple-100 text-purple-800'
};

export default function LeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOperator, setFilterOperator] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const computeDateRange = (preset) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === 'today') return { from: iso(now), to: iso(now) };
    if (preset === 'week') {
      const s = new Date(now); s.setDate(now.getDate() - 6);
      return { from: iso(s), to: iso(now) };
    }
    if (preset === 'month') {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: iso(s), to: iso(now) };
    }
    return { from: '', to: '' };
  };
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    source: 'manual',
    status: 'sem_contato',
    notes: ''
  });

  const isAdmin = user?.role === 'admin';
  const canSeeAllLeads = user?.role === 'admin' || user?.role === 'supervisor' || user?.can_view_all_leads === true;
  const isPrivileged = user?.role === 'admin' || user?.role === 'supervisor';
  const canExport = user?.role === 'admin' || ['admin@biotop.com', 'bruno@biotop.com', 'gestor@biotop.com'].includes(user?.email?.toLowerCase());
  // Filtro de data de chegada — visível para admin, jessica, gestor e eduardo
  const canFilterByDate = ['admin@biotop.com', 'jessica@biotop.com', 'gestor@biotop.com', 'eduardo@biotop.com'].includes(user?.email?.toLowerCase());

  useEffect(() => {
    fetchLeads();
    if (canSeeAllLeads) {
      fetchOperators();
    }
  }, [filterStatus, filterOperator, datePreset, dateFrom, dateTo, page]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchLeads();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchLeads = async () => {
    try {
      const params = { page, limit: 50 };
      if (filterStatus) params.status = filterStatus;
      if (filterOperator) params.assigned_to = filterOperator;
      if (searchTerm) params.search = searchTerm;
      // Filtro de data
      if (datePreset !== 'all' && datePreset !== 'custom') {
        const r = computeDateRange(datePreset);
        params.date_from = r.from;
        params.date_to = r.to;
      } else if (datePreset === 'custom') {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const response = await api.getLeads(params);
      const data = response.data;
      setLeads(data.leads || []);
      setTotalPages(data.pages || 1);
      setTotalLeads(data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperators = async () => {
    try {
      const response = await api.getOperators();
      setOperators(response.data);
    } catch (error) {
      console.error('Erro ao carregar operadores');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createLead(formData);
      toast.success('Lead criado com sucesso!');
      setShowCreateDialog(false);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast.error('Erro ao criar lead');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.updateLead(selectedLead.id, formData);
      toast.success('Lead atualizado com sucesso!');
      setShowEditDialog(false);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast.error('Erro ao atualizar lead');
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Tem certeza que deseja excluir este lead?')) return;
    
    try {
      await api.deleteLead(leadId);
      toast.success('Lead excluído com sucesso!');
      fetchLeads();
    } catch (error) {
      toast.error('Erro ao excluir lead');
    }
  };

  const handleAssign = async (leadId, operatorId) => {
    try {
      await api.assignLead(leadId, operatorId);
      toast.success('Lead atribuído com sucesso!');
      fetchLeads();
    } catch (error) {
      toast.error('Erro ao atribuir lead');
    }
  };

  const handleTransferToMe = async (leadId) => {
    try {
      const res = await api.transferLeadToMe(leadId);
      if (res.data?.changed) {
        toast.success(`Lead transferido para você (era de ${res.data.previous_owner || 'ninguém'})`);
      } else {
        toast.info('Este lead já está com você');
      }
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao puxar lead');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await api.importLeads(file);
      toast.success('Leads importados com sucesso!');
      fetchLeads();
    } catch (error) {
      toast.error('Erro ao importar leads');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.exportLeads();
      saveAs(response.data, 'leads.xlsx');
      toast.success('Leads exportados com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar leads');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      source: 'manual',
      status: 'sem_contato',
      notes: ''
    });
    setSelectedLead(null);
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setFormData({
      name: lead.name,
      phone: lead.phone,
      email: lead.email || '',
      source: lead.source,
      status: lead.status,
      notes: lead.notes || ''
    });
    setShowEditDialog(true);
  };

  const filteredLeads = leads.sort((a, b) => {
    // 1. Leads com mensagens não lidas sempre primeiro
    const aUnread = a.unread_count || 0;
    const bUnread = b.unread_count || 0;
    if (aUnread > 0 && bUnread === 0) return -1;
    if (aUnread === 0 && bUnread > 0) return 1;
    // 2. Entre não lidos: mensagem mais antiga primeiro (quem espera há mais tempo)
    if (aUnread > 0 && bUnread > 0) {
      const aTime = a.last_client_message_at ? new Date(a.last_client_message_at).getTime() : 0;
      const bTime = b.last_client_message_at ? new Date(b.last_client_message_at).getTime() : 0;
      return aTime - bTime;
    }
    // 3. Entre os demais: mais recente no topo, mais antigo embaixo
    const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bCreated - aCreated;
  });

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto" />
            <p className="mt-4 text-slate-600">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Leads
              </h1>
              <p className="text-slate-600" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {totalLeads > 0 ? `${totalLeads} leads no total` : 'Gerencie todos os seus leads'}
              </p>
            </div>

            <div className="flex gap-2">
              {canExport && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('import-file').click()}
                    data-testid="import-leads-button"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importar
                  </Button>
                  <input
                    id="import-file"
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleImport}
                  />

                  <Button
                    variant="outline"
                    onClick={handleExport}
                    data-testid="export-leads-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Exportar
                  </Button>
                </>
              )}

              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-lead-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Lead
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Criar Novo Lead</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                      <Label>Nome</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="lead-name-input"
                      />
                    </div>
                    <div>
                      <Label>Telefone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        data-testid="lead-phone-input"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="lead-email-input"
                      />
                    </div>
                    <div>
                      <Label>Observações</Label>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        data-testid="lead-notes-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-lead-button">
                      Criar Lead
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Filters */}
          <Card className="p-6 mb-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    placeholder="Buscar por nome, telefone ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-leads-input"
                  />
                </div>
              </div>
              <Select value={filterStatus || "all"} onValueChange={(val) => { setPage(1); setFilterStatus(val === "all" ? "" : val); }}>
                <SelectTrigger className="w-48" data-testid="filter-status-select">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.keys(statusLabels).map(status => (
                    <SelectItem key={status} value={status}>
                      {statusLabels[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {canSeeAllLeads && operators.length > 0 && (
                <Select value={filterOperator || "all"} onValueChange={(val) => { setPage(1); setFilterOperator(val === "all" ? "" : val); }}>
                  <SelectTrigger className="w-48" data-testid="filter-operator-select">
                    <SelectValue placeholder="Todos os operadores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos operadores</SelectItem>
                    {operators.map(op => (
                      <SelectItem key={op.id} value={op.id}>
                        {op.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {canFilterByDate && (
                <>
                  <Select
                    value={datePreset}
                    onValueChange={(val) => { setPage(1); setDatePreset(val); }}
                  >
                    <SelectTrigger className="w-44" data-testid="filter-date-preset">
                      <SelectValue placeholder="Todo período" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todo período</SelectItem>
                      <SelectItem value="today">Hoje</SelectItem>
                      <SelectItem value="week">Últimos 7 dias</SelectItem>
                      <SelectItem value="month">Este mês</SelectItem>
                      <SelectItem value="custom">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                  {datePreset === 'custom' && (
                    <>
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
                        className="w-40"
                        data-testid="filter-date-from"
                      />
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
                        className="w-40"
                        data-testid="filter-date-to"
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Mensagens</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Status</TableHead>
                  {canSeeAllLeads && <TableHead>Operador</TableHead>}
                  <TableHead>Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => {
                  const unread = lead.unread_count || 0;
                  const urgency = unread > 0 ? getUrgencyColor(lead.last_client_message_at) : '';
                  const ago = timeAgo(lead.last_client_message_at);
                  const msgType = lead.last_client_message_type;
                  const msgPreview = lead.last_client_message;

                  return (
                  <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`} className={unread > 0 ? 'bg-emerald-50/50' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {unread > 0 && (
                          <span className="flex items-center justify-center min-w-5 h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full" data-testid={`unread-badge-${lead.id}`}>
                            {unread}
                          </span>
                        )}
                        <span>{lead.name}</span>
                        {urgency && (
                          <span className={`w-2.5 h-2.5 rounded-full ${urgency} flex-shrink-0`} data-testid={`urgency-${lead.id}`} title={unread > 0 ? `Aguardando ${ago}` : ''} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {lead.last_client_message_at ? (
                        <div className="max-w-48">
                          <div className={`text-xs truncate ${unread > 0 ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>
                            {msgTypeIcon(msgType)}
                            {msgType && msgType !== 'text'
                              ? (msgType === 'audio' ? 'Audio' : msgType === 'image' ? 'Imagem' : msgType === 'video' ? 'Video' : 'Arquivo')
                              : (msgPreview || '')}
                          </div>
                          <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3" />
                            {ago}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>{lead.phone}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[lead.status]}>
                        {statusLabels[lead.status]}
                      </Badge>
                    </TableCell>
                    {canSeeAllLeads && (
                      <TableCell>
                        {isPrivileged ? (
                          <Select
                            value={lead.assigned_to || "unassigned"}
                            onValueChange={(value) => value !== "unassigned" && handleAssign(lead.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder="Atribuir" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Selecionar...</SelectItem>
                              {operators.map(op => (
                                <SelectItem key={op.id} value={op.id}>
                                  {op.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-sm text-slate-700" data-testid={`lead-owner-${lead.id}`}>
                            {lead.assigned_to_name || <span className="text-slate-400 italic">Sem operador</span>}
                          </span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          data-testid={`view-lead-${lead.id}`}
                        >
                          <Eye className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(lead)}
                          data-testid={`edit-lead-${lead.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {isPrivileged && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(lead.id)}
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4" data-testid="pagination-controls">
              <p className="text-sm text-slate-500">
                Mostrando {((page - 1) * 50) + 1}-{Math.min(page * 50, totalLeads)} de {totalLeads} leads
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="pagination-prev"
                >
                  Anterior
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className={page === pageNum ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                      onClick={() => setPage(pageNum)}
                      data-testid={`pagination-page-${pageNum}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="pagination-next"
                >
                  Proximo
                </Button>
              </div>
            </div>
          )}

          {/* Edit Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Lead</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEdit} className="space-y-4">
                <div>
                  <Label>Nome</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(statusLabels).map(status => (
                        <SelectItem key={status} value={status}>
                          {statusLabels[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Observações</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Salvar Alterações
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
